import { useState } from 'react';
import { Plus, Edit, Calendar, Users, TrendingUp } from 'lucide-react';
import { Badge } from '@medikei/ui';
import { Button } from '@/components/ui/Button';
import { Pagination, PaginationInfo } from '@/components/ui/pagination';
import { LoadingOverlay } from '@/components/ui/loading-overlay';
import { EmptyState } from '@/components/ui/EmptyState';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/Dialog';
import { Input } from '@/components/ui/input';
import type { Campaign } from '../types/admin-partner';

interface CampaignTableProps {
  campaigns: Campaign[];
  isLoading: boolean;
  total: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onCreate: (data: { name: string; description?: string; commissionRate: number; startDate: string; endDate?: string }) => void;
  onUpdate: (id: string, data: Partial<Campaign>) => void;
  isCreating?: boolean;
  isUpdating?: boolean;
}

const statusVariants: Record<Campaign['status'], 'success' | 'warning' | 'outline'> = {
  ACTIVE: 'success',
  INACTIVE: 'outline',
  EXPIRED: 'warning',
};

const statusLabels: Record<Campaign['status'], string> = {
  ACTIVE: 'Ativa',
  INACTIVE: 'Inativa',
  EXPIRED: 'Expirada',
};

export function CampaignTable({
  campaigns,
  isLoading,
  total,
  page,
  pageSize,
  onPageChange,
  onCreate,
  onUpdate,
  isCreating,
  isUpdating,
}: CampaignTableProps) {
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState<Campaign | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    commissionRate: 0,
    startDate: '',
    endDate: '',
  });

  const formatDate = (dateStr: string) => {
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(new Date(dateStr));
  };

  const formatRate = (rate: number) => {
    return `${(rate * 100).toFixed(1)}%`;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(amount);
  };

  const handleOpenEdit = (campaign: Campaign) => {
    setEditingCampaign(campaign);
    setFormData({
      name: campaign.name,
      description: campaign.description || '',
      commissionRate: campaign.commissionRate * 100,
      startDate: campaign.startDate.split('T')[0] || '',
      endDate: campaign.endDate ? campaign.endDate.split('T')[0] || '' : '',
    });
  };

  const handleSave = () => {
    if (editingCampaign) {
      onUpdate(editingCampaign.id, {
        name: formData.name,
        description: formData.description,
        commissionRate: formData.commissionRate / 100,
        startDate: formData.startDate,
        endDate: formData.endDate || undefined,
      });
    } else {
      onCreate({
        name: formData.name,
        description: formData.description,
        commissionRate: formData.commissionRate / 100,
        startDate: formData.startDate,
        endDate: formData.endDate || undefined,
      });
    }
    setShowCreateDialog(false);
    setEditingCampaign(null);
    setFormData({ name: '', description: '', commissionRate: 0, startDate: '', endDate: '' });
  };

  const openCreateDialog = () => {
    setFormData({ name: '', description: '', commissionRate: 0, startDate: '', endDate: '' });
    setEditingCampaign(null);
    setShowCreateDialog(true);
  };

  if (isLoading) {
    return <LoadingOverlay message="Carregando campanhas..." />;
  }

  if (campaigns.length === 0) {
    return (
      <EmptyState
        title="Nenhuma campanha encontrada"
        description="Comece criando sua primeira campanha de parceiros."
        action={{
          label: 'Criar Campanha',
          onClick: openCreateDialog,
        }}
      />
    );
  }

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={openCreateDialog}>
          <Plus className="h-4 w-4 mr-2" />
          Nova Campanha
        </Button>
      </div>

      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full">
          <thead className="bg-muted/50">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Nome</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Taxa</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Período</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Status</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Indicadores</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {campaigns.map((campaign) => (
              <tr key={campaign.id} className="hover:bg-muted/30">
                <td className="px-4 py-3">
                  <p className="font-medium text-sm">{campaign.name}</p>
                  {campaign.description && (
                    <p className="text-xs text-muted-foreground line-clamp-1">
                      {campaign.description}
                    </p>
                  )}
                </td>
                <td className="px-4 py-3 text-sm font-medium">
                  {formatRate(campaign.commissionRate)}
                </td>
                <td className="px-4 py-3 text-sm text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    {formatDate(campaign.startDate)}
                    {campaign.endDate && ` - ${formatDate(campaign.endDate)}`}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <Badge variant={statusVariants[campaign.status]}>
                    {statusLabels[campaign.status]}
                  </Badge>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-4 text-xs">
                    <span className="flex items-center gap-1">
                      <Users className="h-3 w-3" />
                      {campaign.partnerCount}
                    </span>
                    <span className="flex items-center gap-1">
                      <TrendingUp className="h-3 w-3" />
                      {campaign.totalReferrals}
                    </span>
                    <span className="text-muted-foreground">
                      {formatCurrency(campaign.totalCommissions)}
                    </span>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleOpenEdit(campaign)}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between">
        <PaginationInfo
          currentPage={page}
          pageSize={pageSize}
          totalItems={total}
        />
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          onPageChange={onPageChange}
        />
      </div>

      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingCampaign ? 'Editar Campanha' : 'Nova Campanha'}
            </DialogTitle>
            <DialogDescription>
              {editingCampaign
                ? 'Altere os dados da campanha.'
                : 'Crie uma nova campanha para parceiros.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium">Nome</label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Nome da campanha"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Descrição</label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Descrição opcional"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Taxa de Comissão (%)</label>
              <Input
                type="number"
                step="0.1"
                value={formData.commissionRate}
                onChange={(e) =>
                  setFormData({ ...formData, commissionRate: parseFloat(e.target.value) || 0 })
                }
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Data de Início</label>
                <Input
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                />
              </div>
              <div>
                <label className="text-sm font-medium">Data de Término</label>
                <Input
                  type="date"
                  value={formData.endDate}
                  onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleSave}
              disabled={!formData.name || !formData.startDate || isCreating || isUpdating}
              isLoading={isCreating || isUpdating}
            >
              {editingCampaign ? 'Salvar' : 'Criar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
